import { Box, Grid, Typography, SxProps } from "@mui/material";
import { ChangeEventHandler, FC, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { FileRejection } from "react-dropzone";
import { useSnackbar } from "notistack";
import { FileError } from "react-dropzone";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { IAttachment, IMenuOption } from "../../types/global";
import AttachmentCard from "./AttachmentCard";
import dayjs from "dayjs";
import { useStateContext } from "../../contexts/auth-context";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  borderWidth: 2,
  borderRadius: 14,
  borderColor: "#324290",
  borderStyle: "dashed",
  backgroundColor: "#F2F2F2",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
  cursor: "pointer",
  height: 120,
} as const;

const activeStyle = {
  borderColor: "#2196f3",
} as const;

const acceptStyle = {
  borderColor: "#00e676",
} as const;

const rejectStyle = {
  borderColor: "#ff1744",
} as const;

const DocumentDropzoneUI: FC<{
  control: Control<any>;
  name: string;
  setValue: UseFormSetValue<any>;
  multiple?: boolean;
  options?: IMenuOption[];
  acceptedFileType?: string;
  maxSize?: number;
  maxFileSize?: number;
  disabled?: boolean;
  isModal?: boolean;
  customStyle?: any;
  attachmentCardStyle?: SxProps;
  hideIcon?: boolean;
  hideCount?: boolean;
}> = ({
  control,
  name,
  setValue,
  multiple,
  options,
  acceptedFileType,
  maxSize,
  maxFileSize,
  disabled,
  isModal,
  customStyle,
  attachmentCardStyle,
  hideIcon,
  hideCount,
}) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange } }) => (
        <Dropzone
          control={control}
          name={name}
          setValue={setValue}
          multiple={multiple}
          onChange={(e) =>
            onChange(multiple ? e.target.files : e.target.files?.[0] ?? null)
          }
          options={options}
          acceptedFileType={acceptedFileType}
          maxSize={maxSize}
          disabled={disabled}
          maxFileSize={maxFileSize}
          isModal={isModal}
          customStyle={customStyle}
          attachmentCardStyle={attachmentCardStyle}
          hideIcon={hideIcon}
          hideCount={hideCount}
        />
      )}
    />
  );
};

const Dropzone: FC<{
  control: Control;
  name: string;
  setValue: UseFormSetValue<any>;
  multiple?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  options?: IMenuOption[];
  maxSize?: number;
  maxFileSize?: number;
  acceptedFileType?: string;
  isModal?: boolean;
  customStyle?: any;
  attachmentCardStyle?: SxProps;
  hideIcon?: boolean;
  hideCount?: boolean;
}> = ({
  control,
  name,
  setValue,
  multiple,
  onChange,
  disabled,
  options,
  acceptedFileType,
  maxSize,
  maxFileSize,
  isModal,
  customStyle,
  attachmentCardStyle,
  hideIcon,
  hideCount,
}) => {
  // const newDisabled ={}
  const getAcceptFileType = (
    fileType: string | undefined
  ): {
    "image/*"?: any[];
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"?: any[];
    "application/pdf"?: any[];
  } => {
    switch (fileType) {
      case "image":
        return {
          "image/*": [],
        };
      case "xlsx":
        return {
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            [],
        };
      default:
        return {
          "image/*": [],
          "application/pdf": [],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            [],
        };
    }
  };

  const { enqueueSnackbar } = useSnackbar();

  const {
    state: { authUser },
  } = useStateContext();

  const files = useWatch({
    control,
    name,
  });

  // const newDisabled = files.length >= (maxSize || 0);

  const formatBytes = (bytes: number) => {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 Bytes";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const formattedSize = (bytes / Math.pow(1024, i)).toFixed(2);
    return `${formattedSize} ${sizes[i]}`;
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: getAcceptFileType(acceptedFileType),
    maxSize: maxFileSize ? maxFileSize : 10485760,
    onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      fileRejections.forEach((file: any) => {
        file.errors.forEach((err: FileError) => {
          if (err.code === "file-too-large") {
            enqueueSnackbar(
              `ไม่สามารถอัปโหลดได้\nเนื่องจากไฟล์มีขนาดใหญ่เกิน ${formatBytes(
                maxFileSize || 10485760
              )}`,
              {
                variant: "error",
                style: { whiteSpace: "pre-line" },
              }
            );
          }
        });
      });

      const newFiles: (File & IAttachment)[] =
        (!!files?.length && [...files].concat(acceptedFiles)) || acceptedFiles;

      const formatFiles: IAttachment[] = newFiles.map(
        (file: File & IAttachment) => {
          if (file instanceof File)
            return Object.assign(file, {
              attachment_name: file.attachment_name || file.name,
              uploaded_by: {
                user_unique_id: authUser?.unique_id || "",
                first_name: authUser?.first_name || "",
                last_name: authUser?.last_name || "",
              },
              uploaded_date: dayjs(),
              url: URL.createObjectURL(file),
            });
          else return file;
        }
      );

      setValue(name, formatFiles);
    },
    disabled,
    multiple,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...customStyle,
    }),
    [isDragActive, isDragReject, isDragAccept, customStyle]
  );

  const removeFile = (file: any) => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(file), 1);

    setValue(name, newFiles);
  };

  return (
    <Box mt={2}>
      {!disabled && (
        <Box mb={2}>
          <div {...getRootProps({ style })}>
            <input {...getInputProps({ onChange })} />
            {!hideIcon && (
              <ImageOutlinedIcon
                color={customStyle?.color ? customStyle.color : "primary"}
              />
            )}
            {acceptedFileType === "xlsx" ? (
              <Typography
                color={customStyle?.color ? customStyle.color : "primary"}
              >
                อัปโหลดไฟล์ .xlsx
              </Typography>
            ) : (
              <>
                <Typography color="primary">อัปโหลดไฟล์/รูปภาพ</Typography>
              </>
            )}
            {!hideCount && (
              <Typography color="primary">
                {files.length || 0}/{maxSize}
              </Typography>
            )}
          </div>
        </Box>
      )}
      <Grid container spacing={2}>
        {files?.map((file: any, index: number) => (
          <Grid item md={isModal ? 6 : 4} key={index}>
            <AttachmentCard
              control={control}
              disabled={disabled}
              file={file}
              index={index}
              removeFile={removeFile}
              name={name}
              sx={attachmentCardStyle}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DocumentDropzoneUI;
